/*
 * Hero typewriter: turns the heading's <animated-list> into a type / delete / retype loop.
 * Loaded only for the homepage hero (sections/quick-filter.liquid).
 */
(() => {
  const TYPE_MS = 72;        // per character, with jitter below
  const TYPE_JITTER = 34;
  const DELETE_MS = 34;
  const HOLD_TYPED_MS = 2100;
  const HOLD_EMPTY_MS = 320;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  function takeOver(list) {
    if (list.dataset.typewriter === 'on') return;
    list.dataset.typewriter = 'on';

    // Stop the theme's slide rotation
    list.stopAnimation?.();
    list.observer?.disconnect();
    list.startAnimation = () => {};
    list.nextItem = () => {};
    list.setWidth = () => {};

    const words = Array.from(list.querySelectorAll('.animated-list__item'))
      .map((item) => item.textContent.trim())
      .filter(Boolean);
    if (words.length < 2) return;

    list.textContent = '';
    list.style.width = '';
    list.setAttribute('aria-hidden', 'true');

    const text = document.createElement('span');
    text.className = 'hf-type__text';
    text.textContent = words[0];
    const caret = document.createElement('span');
    caret.className = 'hf-type__caret';
    list.append(text, caret);

    // Screen readers get the full list once, not per-keystroke noise
    const spoken = document.createElement('span');
    spoken.className = 'visually-hidden';
    spoken.textContent = words.join(', ');
    list.after(spoken);

    let visible = true;
    const io = new IntersectionObserver(([entry]) => { visible = entry.isIntersecting; }, { threshold: 0.1 });
    io.observe(list);

    const canRun = async () => {
      while (!visible || document.hidden) await sleep(400);
    };

    const setTyping = (on) => list.classList.toggle('is-typing', on);

    (async function loop() {
      let index = 0;
      for (;;) {
        await sleep(HOLD_TYPED_MS);
        await canRun();
        const next = words[(index + 1) % words.length];

        if (reduceMotion.matches) {
          list.classList.add('is-swapping');
          await sleep(180);
          text.textContent = next;
          list.classList.remove('is-swapping');
          index = (index + 1) % words.length;
          continue;
        }

        setTyping(true);
        while (text.textContent.length) {
          text.textContent = text.textContent.slice(0, -1);
          await sleep(DELETE_MS);
        }
        setTyping(false);
        await sleep(HOLD_EMPTY_MS);
        await canRun();

        setTyping(true);
        for (let i = 1; i <= next.length; i++) {
          text.textContent = next.slice(0, i);
          const pauseAfterSpace = next[i - 1] === ' ' ? 60 : 0;
          await sleep(TYPE_MS + Math.round((Math.random() - 0.5) * TYPE_JITTER) + pauseAfterSpace);
        }
        setTyping(false);
        index = (index + 1) % words.length;
      }
    })();
  }

  /*
   * Finder button stays a live teal call to action. The theme disables it until a make is picked;
   * taps on it then land on its wrapper (pointer-events: none in CSS), which opens the Make list.
   */
  function initFinderButton() {
    document.querySelectorAll('[id$="__quick_filter_ggbRjG"] quick-filter').forEach((finder) => {
      const button = finder.querySelector('.js-filter-submit');
      const wrap = button?.parentElement;
      if (!wrap || wrap.dataset.hfCta) return;
      wrap.dataset.hfCta = 'true';
      wrap.classList.add('hf-finder-cta');
      wrap.addEventListener('click', (event) => {
        if (!button.disabled) return;
        event.preventDefault();
        const makeList = finder.querySelector('.nice-select.js-filter-select-1, .quick-filter__select-container .nice-select');
        if (makeList) {
          event.stopPropagation();
          makeList.click();
          makeList.focus?.();
        } else {
          finder.querySelector('select')?.focus();
        }
      });
    });
  }

  /*
   * Phone hero height: CSS sizes the hero to the visible screen minus everything above it (offer bar,
   * header), leaving room for the next section's heading to peek in. This measures "everything above"
   * as --hf-top, on load and whenever the layout above changes size.
   */
  function initHeroFit() {
    document.querySelectorAll('[id$="__quick_filter_ggbRjG"]').forEach((hero) => {
      if (hero.dataset.hfFit) return;
      hero.dataset.hfFit = 'true';
      const measure = () => {
        const top = Math.max(0, Math.round(hero.getBoundingClientRect().top + window.scrollY));
        hero.style.setProperty('--hf-top', `${top}px`);
      };
      measure();
      window.addEventListener('load', measure, { once: true });
      if ('ResizeObserver' in window) {
        const above = document.querySelector('.shopify-section-group-header-group')?.parentElement || document.body;
        new ResizeObserver(measure).observe(above);
      }
    });
  }

  function init() {
    initHeroFit();
    initFinderButton();
    document.querySelectorAll('[id$="__quick_filter_ggbRjG"] animated-list').forEach((list) => {
      customElements.whenDefined('animated-list').then(() => {
        // Let the theme element finish connecting, then replace its behaviour
        requestAnimationFrame(() => takeOver(list));
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();
